"use client"

import { useState, useEffect } from "react"
import { Button, Modal, Form, Table, Card, Spinner } from "react-bootstrap"
import toast from "react-hot-toast"
import axios from "axios"
import { FiEdit, FiTrash2, FiPlusCircle } from "react-icons/fi"

import Sidebar from "../../components/Sidebar"
import Topbar from "../../components/Topbar"

const ShippingZone = () => {
  // Base URL for API requests from environment variables
  const apiUrl = `${
    process.env.REACT_APP_APIURL || "http://localhost:8080"
  }api/shipping-zones`;

  const [shippingZones, setShippingZones] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // State to manage form data for the modal
  const [currentZone, setCurrentZone] = useState({
    minDistance: "",
    maxDistance: "",
    cost: "",
  })

  // Function to fetch all shipping zones from the API
  const fetchZones = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(apiUrl)
      setShippingZones(response.data)
    } catch (error) {
      console.error("Error fetching shipping zones:", error)
      toast.error(error.response?.data?.message || "Could not fetch shipping zones.")
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch zones when the component mounts
  useEffect(() => {
    fetchZones()
  }, [])

  const handleClose = () => {
    setShowModal(false)
    setCurrentZone({ minDistance: "", maxDistance: "", cost: "" }) // Reset form
    setIsEditing(false)
  }

  const handleShow = (zone = null) => {
    if (zone) {
      // For editing, populate form with existing zone data
      setCurrentZone({
        _id: zone._id, // Keep track of the ID for updates
        minDistance: zone.minDistance,
        maxDistance: zone.maxDistance,
        cost: zone.cost,
      })
      setIsEditing(true)
    } else {
      // For adding, form is empty
      setCurrentZone({ minDistance: "", maxDistance: "", cost: "" })
      setIsEditing(false)
    }
    setShowModal(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setCurrentZone({ ...currentZone, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { minDistance, maxDistance, cost } = currentZone

    // Validation
    if (!minDistance || !maxDistance || !cost) {
      toast.error("Please fill all fields.")
      return
    }
    if (Number.parseFloat(minDistance) >= Number.parseFloat(maxDistance)) {
      toast.error("Max distance must be greater than min distance.")
      return
    }

    const zoneData = {
      minDistance: Number.parseFloat(minDistance),
      maxDistance: Number.parseFloat(maxDistance),
      cost: Number.parseFloat(cost),
    }

    try {
      if (isEditing) {
        // Update existing zone
        await axios.put(`${apiUrl}/${currentZone._id}`, zoneData)
        toast.success("Shipping zone updated successfully!")
      } else {
        // Add new zone
        await axios.post(apiUrl, zoneData)
        toast.success("Shipping zone added successfully!")
      }
      fetchZones() // Re-fetch the list to show changes
      handleClose()
    } catch (error) {
      console.error("Error saving shipping zone:", error)
      toast.error(error.response?.data?.message || "Failed to save shipping zone.")
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this shipping zone?")) {
      try {
        await axios.delete(`${apiUrl}/${id}`)
        toast.success("Shipping zone deleted!")
        fetchZones() // Re-fetch the list
      } catch (error) {
        console.error("Error deleting shipping zone:", error)
        toast.error(error.response?.data?.message || "Failed to delete shipping zone.")
      }
    }
  }

  return (
    <div>
      <Topbar />
      <Sidebar />

      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Shipping Zones</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li className="breadcrumb-item">Settings</li>
              <li className="breadcrumb-item active">Shipping Zones</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>Distance-Based Shipping Rates</h5>
              <Button variant="primary" onClick={() => handleShow()}>
                <FiPlusCircle className="me-2" />
                Add New Zone
              </Button>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading zones...</p>
                </div>
              ) : (
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>Min Distance (Mi)</th>
                      <th>Max Distance (Mi)</th>
                      <th>Shipping Cost (£)</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shippingZones.map((zone) => (
                      <tr key={zone._id}>
                        <td>{zone.minDistance}</td>
                        <td>{zone.maxDistance}</td>
                        <td>{Number.parseFloat(zone.cost).toFixed(2)}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleShow(zone)}
                          >
                            <FiEdit /> Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(zone._id)}>
                            <FiTrash2 /> Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </section>

        {/* Modal for Add/Edit Shipping Zone */}
        <Modal show={showModal} onHide={handleClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? "Edit Shipping Zone" : "Add New Shipping Zone"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3" controlId="minDistance">
                <Form.Label>Min Distance (Mi)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 0"
                  name="minDistance"
                  value={currentZone?.minDistance || ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="maxDistance">
                <Form.Label>Max Distance (Mi)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 10"
                  name="maxDistance"
                  value={currentZone?.maxDistance || ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="zoneCost">
                <Form.Label>Shipping Cost (£)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="e.g., 5.99"
                  name="cost"
                  value={currentZone?.cost || ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </main>
    </div>
  )
}

export default ShippingZone
